import { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { DollarSign, Users, Clock, CheckCircle2, XCircle, ExternalLink, Wallet, TrendingUp, Activity } from 'lucide-react';

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtDateTime = (s) => s ? new Date(s).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'all', label: 'All' },
];

/**
 * StaffEarningsPage — admin approval queue for staff withdrawals + live
 * KPIs for commission / payout activity. Mirrors the rhythm of Live
 * Insights so admins have one consistent mental model.
 */
export default function StaffEarningsPage() {
  const [period, setPeriod] = useState('week');
  const [kpis, setKpis] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [perStaff, setPerStaff] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [bulkMax, setBulkMax] = useState(50);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [k, w, s, lb] = await Promise.all([
        api.get(`/api/admin/staff-earnings/kpis?period=${period}`),
        api.get(`/api/admin/staff-earnings/withdrawals${statusFilter ? `?status=${statusFilter}` : ''}`),
        api.get('/api/admin/staff-earnings/per-staff'),
        api.get(`/api/admin/staff-earnings/leaderboard?period=${period}&limit=10`),
      ]);
      setKpis(k.data);
      setWithdrawals(w.data?.withdrawals || []);
      setPerStaff(s.data?.rows || []);
      setLeaderboard(lb.data?.rows || []);
    } catch (err) {
      toast.error('Failed to load staff earnings');
    } finally {
      setLoading(false);
    }
  }, [period, statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);
  // Auto-poll every 30s for live feel (same cadence as other admin pages).
  useEffect(() => {
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [refresh]);

  const doBulkApprove = async () => {
    const amt = parseFloat(bulkMax);
    if (!amt || amt <= 0) return toast.error('Enter a valid max amount');
    if (!window.confirm(`Auto-approve ALL pending withdrawals ≤ $${amt.toFixed(2)}? This action is irreversible.`)) return;
    setBulkSubmitting(true);
    try {
      const r = await api.post(`/api/admin/staff-earnings/withdrawals/bulk-approve?max_amount_usd=${amt}&admin_note=bulk+approved`);
      toast.success(`Approved ${r.data.approved} · Skipped ${r.data.skipped}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.detail || 'Bulk approval failed');
    } finally {
      setBulkSubmitting(false);
    }
  };

  if (loading) return <div className="p-6"><div className="spinner" /></div>;

  return (
    <div className="p-5 md:p-6 space-y-5" data-testid="staff-earnings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Wallet className="w-6 h-6 text-[#D4AF37]" /> Staff Earnings
          </h1>
          <p className="text-sm text-gray-400 mt-0.5">Commission ledger, withdrawal queue, and live KPIs.</p>
        </div>
        <div className="flex rounded-lg p-1 bg-white/[0.03] border border-white/5" data-testid="se-period-switch">
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${period === p.key ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'}`}
              data-testid={`se-period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="se-kpis">
        <KpiCard icon={DollarSign} label="Earned" value={fmtUSD(kpis?.earned_usd)} sub={`${kpis?.commission_events || 0} events`} color="#F5D76E" testid="se-kpi-earned" />
        <KpiCard icon={TrendingUp} label="Withdrawn" value={fmtUSD(kpis?.withdrawn_usd)} color="#4ade80" testid="se-kpi-withdrawn" />
        <KpiCard icon={Clock} label="Pending Queue" value={(kpis?.pending_withdrawals_count ?? 0).toString()} sub={fmtUSD(kpis?.pending_reservations_usd)} color="#fbbf24" testid="se-kpi-pending" />
        <KpiCard icon={Users} label="Active Staff" value={(kpis?.active_staff_count ?? 0).toString()} color="#a5b4fc" testid="se-kpi-staff" />
        <KpiCard icon={Activity} label="Top Agent" value={kpis?.top_staff?.[0]?.username || '—'} sub={fmtUSD(kpis?.top_staff?.[0]?.earned_usd)} color="#f472b6" testid="se-kpi-top" />
      </div>

      {/* Leaderboard — top referral staff by earnings + retention insights. */}
      <div className="rounded-xl p-4" style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.06), rgba(99,102,241,0.06))', border: '1px solid rgba(212,175,55,0.18)' }} data-testid="se-leaderboard">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#F5D76E]" /> Staff Leaderboard
          </h2>
          <span className="text-[10px] uppercase tracking-wide text-gray-500">{period}</span>
        </div>
        {leaderboard.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4" data-testid="se-leaderboard-empty">No earnings yet in this window.</p>
        ) : (
          <div className="space-y-1.5" data-testid="se-leaderboard-list">
            {leaderboard.map(r => (
              <div key={r.staff_id} className="flex items-center gap-3 rounded-lg p-2.5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }} data-testid={`se-lb-${r.staff_id}`}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0" style={{
                  background: r.rank === 1 ? 'linear-gradient(135deg, #F5D76E, #D4AF37)' : r.rank === 2 ? 'rgba(229,231,235,0.15)' : r.rank === 3 ? 'rgba(251,146,60,0.18)' : 'rgba(255,255,255,0.04)',
                  color: r.rank === 1 ? '#0d1117' : '#fff',
                }}>
                  {r.rank}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-semibold truncate">{r.username || '—'}</p>
                    <span className="text-[10px] text-gray-500">{r.referral_code}</span>
                    {r.upline_staff_id && <span className="text-[9px] uppercase tracking-wide px-1.5 py-[1px] rounded font-semibold bg-purple-500/20 text-purple-300">Downline</span>}
                  </div>
                  <p className="text-[10px] text-gray-500">
                    {r.first_deposits} first deps · {r.players_referred} referred · {r.retention_rate}% deposited
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-base font-bold text-[#F5D76E] tabular-nums">{fmtUSD(r.earned_usd + r.override_usd)}</p>
                  {r.override_usd > 0 && <p className="text-[10px] text-purple-300">+{fmtUSD(r.override_usd)} override</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Withdrawal queue */}
      <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-lg font-semibold text-white">Withdrawal Requests</h2>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Bulk approve cluster — only shows when there are pending rows. */}
            {statusFilter === 'pending' && withdrawals.length > 0 && (
              <div className="flex items-center gap-1.5 rounded-lg bg-white/[0.03] border border-white/5 p-1" data-testid="se-bulk-approve-group">
                <span className="text-[10px] uppercase tracking-wide text-gray-400 px-1">Auto-approve ≤</span>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={bulkMax}
                  onChange={e => setBulkMax(e.target.value)}
                  className="bg-[#0B0E14] border-white/10 text-white w-20 h-7 text-xs text-right"
                  data-testid="se-bulk-max-input"
                />
                <Button
                  size="sm"
                  onClick={doBulkApprove}
                  disabled={bulkSubmitting}
                  className="bg-[#D4AF37] hover:bg-[#F5D76E] text-black text-xs h-7"
                  data-testid="se-bulk-approve-btn"
                >
                  {bulkSubmitting ? 'Working…' : 'Bulk Approve'}
                </Button>
              </div>
            )}
            <div className="flex rounded-lg p-1 bg-white/[0.03] border border-white/5" data-testid="se-status-filter">
              {['pending', 'approved', 'rejected', ''].map(s => (
                <button
                  key={s || 'all'}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${statusFilter === s ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'}`}
                  data-testid={`se-status-${s || 'all'}`}
                >
                  {s || 'All'}
                </button>
              ))}
            </div>
          </div>
        </div>
        {withdrawals.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8" data-testid="se-empty">No requests in this view.</p>
        ) : (
          <div className="space-y-2" data-testid="se-withdrawals-list">
            {withdrawals.map(w => <WithdrawalRow key={w.id} w={w} onOpen={() => setSelected(w)} />)}
          </div>
        )}
      </div>

      {/* Per-staff ledger */}
      <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <h2 className="text-lg font-semibold text-white mb-3">Per-Staff Balances</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="se-per-staff-table">
            <thead>
              <tr className="border-b border-white/10 text-left">
                <th className="py-2 px-2 text-xs text-gray-400 font-semibold uppercase">Staff</th>
                <th className="py-2 px-2 text-xs text-gray-400 font-semibold uppercase text-right">Rate</th>
                <th className="py-2 px-2 text-xs text-gray-400 font-semibold uppercase text-right">Earned</th>
                <th className="py-2 px-2 text-xs text-gray-400 font-semibold uppercase text-right">Withdrawn</th>
                <th className="py-2 px-2 text-xs text-gray-400 font-semibold uppercase text-right">Reserved</th>
                <th className="py-2 px-2 text-xs text-gray-400 font-semibold uppercase text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {perStaff.length === 0 ? (
                <tr><td colSpan={6} className="py-6 text-center text-gray-500 text-sm">No referral staff yet.</td></tr>
              ) : perStaff.map(r => (
                <tr key={r.staff_id} className="border-b border-white/[0.03] hover:bg-white/[0.02]" data-testid={`se-staff-${r.staff_id}`}>
                  <td className="py-2 px-2">
                    <p className="text-white font-semibold">{r.username}</p>
                    <p className="text-[10px] text-gray-500">{r.referral_code}</p>
                  </td>
                  <td className="py-2 px-2 text-right text-gray-300 text-xs">{r.commission_percent != null ? `${r.commission_percent}%` : <span className="text-gray-600">default</span>}</td>
                  <td className="py-2 px-2 text-right text-[#F5D76E] font-semibold tabular-nums">{fmtUSD(r.earned_usd)}</td>
                  <td className="py-2 px-2 text-right text-green-400 tabular-nums">{fmtUSD(r.withdrawn_usd)}</td>
                  <td className="py-2 px-2 text-right text-yellow-400 tabular-nums">{fmtUSD(r.reserved_usd)}</td>
                  <td className="py-2 px-2 text-right text-white font-bold tabular-nums">{fmtUSD(r.balance_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <WithdrawalModal
          w={selected}
          onClose={() => setSelected(null)}
          onDecided={() => { setSelected(null); refresh(); }}
        />
      )}
    </div>
  );
}


function KpiCard({ icon: Icon, label, value, sub, color, testid }) {
  return (
    <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" style={{ color }} />
        <p className="text-[10px] uppercase tracking-wide text-gray-400">{label}</p>
      </div>
      <p className="text-xl font-bold text-white tabular-nums truncate">{value}</p>
      {sub != null && <p className="text-[10px] text-gray-500 truncate">{sub}</p>}
    </div>
  );
}


function WithdrawalRow({ w, onOpen }) {
  const badge = w.status === 'approved'
    ? { bg: 'bg-green-500/20', fg: 'text-green-400', icon: CheckCircle2 }
    : w.status === 'rejected'
    ? { bg: 'bg-red-500/20', fg: 'text-red-400', icon: XCircle }
    : { bg: 'bg-yellow-500/20', fg: 'text-yellow-400', icon: Clock };
  return (
    <div
      className="rounded-lg p-3 flex items-center justify-between cursor-pointer hover:bg-white/[0.04] transition"
      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
      onClick={onOpen}
      data-testid={`se-wd-row-${w.id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${badge.bg} flex-shrink-0`}>
          <badge.icon className={`w-5 h-5 ${badge.fg}`} />
        </div>
        <div className="min-w-0">
          <p className="text-white font-bold tabular-nums">{fmtUSD(w.amount_usd)}</p>
          <p className="text-xs text-gray-400 truncate">{w.staff_username} · {w.staff_referral_code}</p>
          <p className="text-[10px] text-gray-500">{fmtDateTime(w.created_at)}</p>
        </div>
      </div>
      <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-1 rounded-full ${badge.bg} ${badge.fg} flex-shrink-0`}>{w.status}</span>
    </div>
  );
}


function WithdrawalModal({ w, onClose, onDecided }) {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const decide = async (action) => {
    setSubmitting(true);
    try {
      await api.post(`/api/admin/staff-earnings/withdrawals/${w.id}/${action}`, { admin_note: note || null });
      toast.success(`Withdrawal ${action}d`);
      onDecided();
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.detail || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const pending = w.status === 'pending';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }} onClick={onClose}>
      <div className="w-full max-w-xl rounded-2xl p-5 space-y-3 max-h-[90vh] overflow-y-auto" style={{ background: '#13111C', border: '1px solid rgba(212,175,55,0.3)' }} onClick={e => e.stopPropagation()} data-testid="se-decision-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Withdrawal #{w.id.slice(0, 8)}</h3>
          <button className="text-gray-400 hover:text-white text-xl" onClick={onClose}>×</button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] text-gray-400 uppercase">Amount</p>
            <p className="text-2xl font-bold text-[#F5D76E] tabular-nums">{fmtUSD(w.amount_usd)}</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-400 uppercase">Status</p>
            <p className="text-lg font-bold uppercase" style={{ color: w.status === 'approved' ? '#4ade80' : w.status === 'rejected' ? '#f87171' : '#fbbf24' }}>{w.status}</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-400 uppercase">Staff</p>
            <p className="text-sm text-white font-medium">{w.staff_username}</p>
            <p className="text-[10px] text-gray-500">{w.staff_email} · {w.staff_referral_code}</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-400 uppercase">Requested</p>
            <p className="text-sm text-white">{fmtDateTime(w.created_at)}</p>
            {w.decided_at && <p className="text-[10px] text-gray-500">Decided {fmtDateTime(w.decided_at)} by {w.decided_by}</p>}
          </div>
        </div>

        {w.note && (
          <div>
            <p className="text-[10px] text-gray-400 uppercase">Staff note</p>
            <p className="text-sm text-gray-200 italic">“{w.note}”</p>
          </div>
        )}

        <div>
          <p className="text-[10px] text-gray-400 uppercase mb-1">QR / Payout proof</p>
          {w.qr_image ? (
            w.qr_image.startsWith('data:') ? (
              <img src={w.qr_image} alt="QR" className="max-w-xs rounded-lg border border-white/10" data-testid="se-wd-qr-img" />
            ) : (
              <a href={w.qr_image} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#F5D76E] hover:underline text-sm" data-testid="se-wd-qr-link">
                <ExternalLink className="w-3.5 h-3.5" /> Open image
              </a>
            )
          ) : <p className="text-xs text-gray-500">No QR attached</p>}
        </div>

        {w.admin_note && (
          <div className="rounded-lg p-2 bg-white/[0.02] border border-white/5">
            <p className="text-[10px] text-gray-400 uppercase">Previous admin note</p>
            <p className="text-sm text-gray-300 italic">“{w.admin_note}”</p>
          </div>
        )}

        {pending && (
          <>
            <div>
              <label className="text-[10px] text-gray-400 uppercase block mb-1">Admin note (optional)</label>
              <Textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="Reason, TRN, or payout instructions…" maxLength={500} className="bg-[#0B0E14] border-white/10 text-white text-sm" data-testid="se-admin-note-input" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={() => decide('reject')} disabled={submitting} variant="outline" className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="se-reject-btn">
                <XCircle className="w-4 h-4 mr-2" /> Reject
              </Button>
              <Button onClick={() => decide('approve')} disabled={submitting} className="flex-1 bg-[#D4AF37] hover:bg-[#F5D76E] text-black font-bold" data-testid="se-approve-btn">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Approve & Pay
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
